import React from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Iconify from '../../components/Iconify';

import Page from '../../components/Page';
import PromotionFilter from '../../components/promotion/PromotionFilterSidebar';
import { PromotionService } from '../../Services/promotion.service';
import PromotionCard from '../../sections/@dashboard/promotion/PromotionCard';
import { useLoadingContext } from '../../context/loadingContext';

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

const Promotion = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [count, setCount] = React.useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = React.useState(false);
  const [filter, setFilter] = React.useState({});
  const [search, setSearch] = React.useState('');
  const [promotions, setPromotions] = React.useState([]);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 10,
  });
  const { handleStartLoading, handleStopLoading } = useLoadingContext();


  React.useEffect(() => {
    GetPromotion().finally(handleStopLoading);
  }, [pagination, search, filter]);

  const GetPromotion = async () => {
    handleStartLoading()
    const filterData = {
      ...pagination,
      search,
      ...filter,
    };
    const promotionsData = await PromotionService.GetPromotion(filterData);
    setPromotions(promotionsData.data);
    setCount(promotionsData.pagination.pageCount);
  };

  const handleOpenFilter = React.useCallback(() => setIsOpen(true), []);
  const handleCloseFilter = React.useCallback(() => setIsOpen(false), []);

  const handleEnablePromotion = React.useCallback(
    (id) => {
      handleStartLoading()
      PromotionService.EnablePromotion(id)
        .then(() => {
          console.log({ promotions });
          const newData = promotions.map((item) => {
            if (item._id === id) item.status = 'Active';
            return item;
          });
          console.log({ newData });
          setPromotions(newData);
          enqueueSnackbar('Thành công', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
        .finally(handleStopLoading)
    },
    [promotions]
  );
  const handleDisablePromotion = React.useCallback(
    (id) => {
      handleStartLoading()
      PromotionService.DisablePromotion(id)
        .then(() => {
          const newData = promotions.map((item) => {
            if (item._id === id) item.status = 'Inactive';
            return item;
          });
          setPromotions(newData);
          enqueueSnackbar('Thành công', { variant: 'success' });
        })
        .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
        .finally(handleStopLoading)
    },
    [promotions]
  );

  const handleDeletePromotion = React.useCallback(
    (id) =>
      Swal.fire({
        customClass: {
          container: { zIndex: 999999 },
        },
        title: 'Xóa bài viết',
        text: 'Thao tác này không thể hoàn tác',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('sure.yes'),
        cancelButtonText: t('sure.no'),
      }).then((result) => {
        handleStartLoading()
        if (result.isConfirmed) {
          PromotionService.DeletePromotion(id)
            .then(() => {
              const newData = promotions.filter((promotion) => promotion._id !== id);
              setPromotions(newData);
              enqueueSnackbar('Thành công', { variant: 'success' });
            })
            .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
            .finally(handleStopLoading);
        }
      }),
    [promotions]
  );

  const handleEditPromotion = React.useCallback((slug) => navigate(`/promotion/edit/${slug}`), []);

  const typingTimeoutRef = React.useRef();

  const handleSetDefaultPage = () => {
    setPagination((prevState) => ({
      ...prevState,
      page: 1,
    }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setPagination((prevState) => ({
        ...prevState,
        page: 1,
      }));
    }, 500);
  };

  return (
    <Page title={'Promotion'}>
      <PromotionFilter
        isOpenFilter={isOpen}
        filter={filter}
        onOpenFilter={handleOpenFilter}
        onCloseFilter={handleCloseFilter}
        setFilter={setFilter}
        setDefaultPage={handleSetDefaultPage}
      />
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Khuyến mãi
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/promotion/create"
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Thêm khuyến mãi
          </Button>
        </Stack>
        <Stack mb={5} direction="row" alignItems="center" justifyContent="space-between">
          <SearchStyle
            onChange={handleSearchChange}
            placeholder={'Tìm kiếm'}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
              </InputAdornment>
            }
          />

          <Stack direction="row" spacing={0}>
            <Tooltip arrow title={'Bộ lọc'}>
              <IconButton onClick={handleOpenFilter}>
                <Iconify icon="ic:round-filter-list" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {promotions.map((promotion) => (
          <PromotionCard
            key={promotion._id}
            promotion={promotion}
            onEdit={handleEditPromotion}
            onDisable={handleDisablePromotion}
            onEnable={handleEnablePromotion}
            onDelete={handleDeletePromotion}
          />
        ))}
        <Stack mt={3} justifyContent={'flex-end'} alignItems={'end'}>
          <Pagination count={count} color="primary" onChange={(_, page) => setPagination({ ...pagination, page })} />
        </Stack>
      </Container>
    </Page>
  );
};
export default Promotion;
